import { useContext } from "react";
import { Card } from "react-bootstrap";
import clsx from "clsx";
import Eyebrow from "@/components/Eyebrow";
import { MetricSourceContext, type MetricSource } from "@/components/analytics/metricSource";
import QueryState, { type QueryStateTile } from "@/components/analytics/QueryState";
import Sparkline from "@/components/analytics/Sparkline";
import { TileSourceLine } from "@/components/analytics/TileSource";
import { NO_AS_OF_TIME, buildAsOfFooter } from "@/lib/analytics/freshness";
import { metricDelta, type DeltaTone, type GoodDirection } from "@/lib/analytics/metricDelta";

type MetricCardProps = {
  label: string;
  value: string | number;
  helper?: string;
  delta?: number;
  deltaLabel?: string;
  // --- v2 (ANL-E04-S09). Every one is optional: without them the card renders as before. ---
  /** A rate's change is in points ("+0.4 pt"); a count's in relative percent (the default). */
  deltaKind?: "count" | "rate";
  /** Which way is good: a rise in a drop-off rate is negative. Up by default. */
  goodDirection?: GoodDirection;
  sparkline?: readonly number[];
  /** The tile's source kind and meta; defaults to the nearest `MetricSourceContext`. */
  source?: MetricSource;
  /** A non-data S08 tile state (Loading, Error, Not measured, ...) renders in place of the value. */
  tile?: QueryStateTile;
  onRetry?: () => void;
  /** Epoch ms the freshness chip judges age against; the page's shared clock by default. */
  now?: number;
};

// The neutral word adds `--ink`: muted text reads 4.16:1 on the sand chip fill (DSU-01, ANL-E04-S11).
const DELTA_CHIP_CLASS: Record<DeltaTone, string> = {
  positive: "pz-chip pz-chip--positive",
  negative: "pz-chip pz-chip--negative",
  neutral: "pz-chip pz-chip--neutral pz-chip--ink",
};

const hasDelta = (delta?: number | null): delta is number =>
  delta !== undefined && delta !== null && !Number.isNaN(delta);

const isZeroValue = (value: string | number) => {
  if (typeof value === "number") return value === 0;
  return /^[\s$]*[0.,]+\s*%?$/.test(value);
};

const missingAsOf = (source: MetricSource): QueryStateTile => ({
  body: "error",
  message: NO_AS_OF_TIME,
  detail: null,
  queryId: source.meta?.queryId ?? null,
  actions: [{ kind: "retry", label: "Retry" }],
});

/**
 * One headline number (CMP-11.04). v2 adds the tile states, the as-of footer, the freshness chip
 * and the source popover once the card has a source (a `source` prop or a tile frame's context),
 * and never shows a value without its time. It imports nothing from the chart library: eagerly
 * loaded pages render it (AC6).
 */
const MetricCard = ({
  label,
  value,
  helper,
  delta,
  deltaLabel,
  deltaKind,
  goodDirection,
  sparkline,
  source: sourceProp,
  tile,
  onRetry,
  now,
}: MetricCardProps) => {
  const contextSource = useContext(MetricSourceContext);
  // A tier E or I region with no routes renders nothing at all (STATE-26).
  if (tile?.body === "region-absent") return null;
  const source = sourceProp ?? contextSource;
  const footer = source ? buildAsOfFooter([source.meta]) : null;
  const state = tile && tile.body !== "data" ? tile : source && footer && !footer.ok ? missingAsOf(source) : null;

  if (state) {
    return (
      <Card className="pz-stat" data-metric-state={state.body}>
        <Card.Body>
          <Eyebrow>{label}</Eyebrow>
          <QueryState tile={state} label={label} onRetry={onRetry} />
        </Card.Body>
      </Card>
    );
  }

  const change = hasDelta(delta) ? metricDelta({ change: delta, kind: deltaKind, goodDirection }) : null;
  return (
    <Card className="pz-stat">
      <Card.Body>
        <Eyebrow>{label}</Eyebrow>
        <div className={clsx("pz-stat__value", isZeroValue(value) && "pz-stat__value--zero")}>
          {value}
        </div>
        {sparkline ? <Sparkline values={sparkline} label={`${label} trend`} /> : null}
        {change || deltaLabel ? (
          <div className="pz-stat__meta">
            {change ? <span className={DELTA_CHIP_CLASS[change.tone]}>{change.text}</span> : null}
            {deltaLabel ? <span>{deltaLabel}</span> : null}
          </div>
        ) : null}
        {helper ? <div className="pz-stat__helper">{helper}</div> : null}
        {source && footer?.ok ? (
          <TileSourceLine label={label} source={source} asOf={footer.text} now={now} />
        ) : null}
      </Card.Body>
    </Card>
  );
};

export default MetricCard;
